import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Sun, FileText, ArrowRight, Plus, Calendar, MessageSquare, CreditCard, Gift,
  Lock, CheckCircle2, Clock, Sparkles
} from "lucide-react";
import StatusTimeline from "@/components/StatusTimeline";
import TestimonialPrompt from "@/components/TestimonialPrompt";
import HurricaneBanner from "@/components/HurricaneBanner";
import SavingsHero from "@/components/SavingsHero";
import CompletionCelebration from "@/components/CompletionCelebration";
import CountUp from "@/components/CountUp";
import { motion } from "framer-motion";
import { stagger, cardRise } from "@/lib/motion";

const STATUS_COLOR = {
  ordered: "bg-slate-200 text-slate-700",
  in_process: "bg-blue-100 text-blue-700",
  awaiting_arrival: "bg-amber-100 text-amber-700",
  scheduling: "bg-purple-100 text-purple-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  installed: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

function PendingClientWelcome({ user }) {
  const [requests, setRequests] = useState([]);
  useEffect(() => {
    api.get("/requests/my").then((r) => setRequests(r.data)).catch(() => {});
  }, []);

  const hasPendingRequest = requests.length > 0;

  return (
    <div className="space-y-8" data-testid="pending-client-welcome">
      {/* Hero card */}
      <div className="card-pb p-8 md:p-10 bg-gradient-to-br from-[#0A2540] to-[#103960] text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#C9A356]/15 blur-3xl pointer-events-none"></div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A356]/15 border border-[#C9A356]/30 rounded-full mb-5">
            <Lock className="w-3 h-3 text-[#C9A356]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#C9A356]">Awaiting Approval</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight">
            Welcome to PowerBuilt, {user.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="mt-4 text-slate-300 max-w-xl leading-relaxed">
            Your account is active. Take the savings calculator and book a free consultation —
            our team will visit, finalize your system, and unlock pricing & full features once approved.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-xs text-[#C9A356]">
            <span className="font-mono" data-testid="pending-customer-code">{user.customer_code}</span>
            <span className="text-white/40">·</span>
            <span>Account opened {new Date(user.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* 3-step flow */}
      <div>
        <p className="overline mb-3">How it works</p>
        <h2 className="font-heading text-2xl text-[#0A2540] tracking-tight mb-6">Three steps to get started</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              n: 1, icon: Sun, title: "Estimate your savings",
              body: "60-second calculator gives you projected annual & 25-year savings.",
              cta: "Open calculator", to: "/dashboard/calculator", complete: true, testid: "step-calc"
            },
            {
              n: 2, icon: Calendar, title: "Book consultation",
              body: "Tell us about your home. We schedule a free site visit at your convenience.",
              cta: hasPendingRequest ? "Request submitted ✓" : "Schedule now",
              to: "/dashboard/services",
              complete: hasPendingRequest, testid: "step-consult"
            },
            {
              n: 3, icon: CheckCircle2, title: "Get approved",
              body: "After consultation, your account unlocks: full packages, pricing, payments, project tracking.",
              cta: null,
              complete: false, testid: "step-approve"
            },
          ].map((s) => (
            <div key={s.testid} className={`card-pb p-6 transition-all ${s.complete ? "border-2 border-[#C9A356]" : ""}`} data-testid={s.testid}>
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-md grid place-items-center ${s.complete ? "bg-[#C9A356] text-white" : "bg-[#F3E8CE] text-[#C9A356]"}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Step {s.n}</span>
              </div>
              <h3 className="font-heading font-medium text-lg text-[#0A2540]">{s.title}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.body}</p>
              {s.to && (
                <Link to={s.to} className={`mt-5 inline-flex items-center gap-2 text-sm font-medium ${s.complete ? "text-[#10B981]" : "text-[#0A2540] hover:text-[#C9A356]"}`}>
                  {s.cta} {!s.complete && <ArrowRight className="w-4 h-4" />}
                </Link>
              )}
              {!s.to && (
                <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#94A3B8]">
                  <Lock className="w-3.5 h-3.5" /> {s.cta || "Locked"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pending request summary if exists */}
      {hasPendingRequest && (
        <div className="card-pb p-6 bg-[#FFFBF0] border-[#C9A356]/40" data-testid="consultation-pending-card">
          <div className="flex items-start gap-4">
            <Clock className="w-6 h-6 text-[#C9A356] shrink-0" />
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#0A2540]">Consultation request received</h3>
              {requests[0]?.appointment_at ? (
                <p className="text-sm text-[#475569] mt-1" data-testid="appointment-confirmed-text">
                  Booked for{" "}
                  <span className="font-semibold text-[#0A2540]">
                    {new Date(requests[0].appointment_at).toLocaleString([], { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </span>. We'll see you then!
                </p>
              ) : (
                <p className="text-sm text-[#475569] mt-1">
                  Submitted {new Date(requests[0].created_at).toLocaleString()}.
                  Our team will be in touch within 1 business day to schedule your site visit.
                </p>
              )}
              <div className="mt-4 flex gap-3">
                <Link to="/dashboard/messages" className="btn-outline-navy text-sm py-2 px-4" data-testid="open-chat-cta">
                  <MessageSquare className="w-4 h-4 mr-2" /> Message team
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/dashboard/calculator" className="card-pb p-5 hover:-translate-y-0.5 transition-all flex items-center gap-3" data-testid="quick-calc">
          <div className="w-10 h-10 rounded-md bg-[#F3E8CE] text-[#C9A356] grid place-items-center"><Sparkles className="w-5 h-5" /></div>
          <div>
            <div className="font-medium text-[#0A2540] text-sm">Try the savings calculator</div>
            <div className="text-xs text-[#475569]">60 seconds · personalized estimate</div>
          </div>
        </Link>
        <Link to="/dashboard/messages" className="card-pb p-5 hover:-translate-y-0.5 transition-all flex items-center gap-3" data-testid="quick-msg">
          <div className="w-10 h-10 rounded-md bg-[#F3E8CE] text-[#C9A356] grid place-items-center"><MessageSquare className="w-5 h-5" /></div>
          <div>
            <div className="font-medium text-[#0A2540] text-sm">Message our team</div>
            <div className="text-xs text-[#475569]">Direct line · same business day</div>
          </div>
        </Link>
      </div>
    </div>
  );
}

export default function ClientOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (user.access_level !== "approved") return; // skip API calls that need approval
    (async () => {
      const [pr, inv, act] = await Promise.all([
        api.get("/projects/my").catch(() => ({ data: [] })),
        api.get("/invoices/my").catch(() => ({ data: [] })),
        api.get("/activity/my").catch(() => ({ data: [] })),
      ]);
      setProjects(pr.data);
      setInvoices(inv.data);
      setActivity(act.data.slice(0, 5));
    })();
  }, [user]);

  // If user just signed up via the savings calculator pre-fill, take them straight to the prefilled form
  useEffect(() => {
    const raw = localStorage.getItem("pb_calc_intent");
    if (raw) {
      try {
        const data = JSON.parse(raw);
        if (Date.now() - data.ts < 86400 * 1000) {
          navigate("/dashboard/services", { replace: true });
        }
      } catch (err) {
        console.warn("Calc-intent redirect skipped:", err);
      }
    }
  }, [navigate]);

  if (!user) return null;

  // Pending client → simplified welcome
  if (user.role === "client" && user.access_level !== "approved") {
    return <PendingClientWelcome user={user} />;
  }

  // Approved client → full overview
  const openInvoices = invoices.filter((i) => i.status === "open");
  const activeProject = projects.find((p) => !["completed", "cancelled"].includes(p.status)) || projects[0];

  return (
    <div className="space-y-8" data-testid="client-overview">
      <TestimonialPrompt />
      <HurricaneBanner compact />
      <CompletionCelebration />

      {/* HERO — premium gradient with subtle radial spotlight */}
      <div className="card-pb p-7 md:p-10 text-white relative overflow-hidden pb-spotlight border-0" data-testid="client-hero">
        <div className="absolute -top-40 -right-32 w-[26rem] h-[26rem] rounded-full bg-[#C9A356]/10 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A356]/40 to-transparent"></div>
        <div className="relative flex flex-wrap gap-8 justify-between items-start">
          <div className="pb-rise pb-rise-1">
            <p className="overline text-[#C9A356]">Welcome back</p>
            <h1 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight mt-2">
              Hello, {user.name?.split(" ")[0] || "there"}
            </h1>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full backdrop-blur-sm">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A356] font-bold">Customer ID</span>
              <span className="font-mono text-sm" data-testid="overview-customer-code">{user.customer_code || "—"}</span>
            </div>
            <div className="text-xs text-white/60 mt-2">{user.email}</div>
          </div>

          {/* Stats — glass tiles */}
          <motion.div className="grid grid-cols-2 gap-3 sm:gap-4" variants={stagger(0.08)} initial="hidden" animate="show">
            {[
              { label: "Active", sub: "Projects", value: projects.filter(p => !["completed","cancelled"].includes(p.status)).length, testid: "stat-active-projects", accent: false },
              { label: "Open", sub: "Invoices", value: openInvoices.length, testid: "stat-open-invoices", accent: true },
            ].map((s) => (
              <motion.div key={s.label} variants={cardRise}
                          className={`px-5 py-4 rounded-xl border backdrop-blur-md ${s.accent ? "bg-[#C9A356]/15 border-[#C9A356]/40" : "bg-white/5 border-white/10"}`}>
                <div className="overline text-[#C9A356]">{s.label}</div>
                <div className={`font-heading text-3xl font-bold mt-1 tabular-nums ${s.accent ? "text-[#C9A356]" : ""}`} data-testid={s.testid}>
                  <CountUp value={s.value} duration={1.1} />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{s.sub}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Emotional impact hero — only renders if client has completed solar projects */}
      <SavingsHero />

      {/* QUICK ACTIONS — refined hover/active states */}
      <motion.div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" variants={stagger(0.06)} initial="hidden" animate="show">
        {[
          { to: "/dashboard/services", label: "Request Quote", sub: "60s · free estimate", icon: Plus, accent: true },
          { to: "/dashboard/services", label: "Book Site Visit", sub: "We come to you", icon: Calendar },
          { to: "/dashboard/messages", label: "Message Team", sub: "Live response", icon: MessageSquare },
          { to: "/dashboard/payments", label: "Make Payment", sub: "Secure · Stripe", icon: CreditCard },
        ].map((q, i) => (
          <motion.div key={q.label} variants={cardRise}>
            <Link
              to={q.to} data-testid={`quick-action-${i}`}
              className={`group relative overflow-hidden card-pb p-5 block transition-all hover:-translate-y-1 hover:shadow-lg ${q.accent ? "border-2 border-[#C9A356] bg-gradient-to-br from-white to-[#FFFBF0] dark:from-slate-800 dark:to-[#3a2e16]" : ""}`}
            >
              <div className={`w-11 h-11 rounded-lg grid place-items-center mb-3 transition-transform group-hover:scale-110 group-hover:rotate-3 ${q.accent ? "bg-[#C9A356] text-white" : "bg-[#F3E8CE] dark:bg-[#3a2e16] text-[#C9A356]"}`}>
                <q.icon className="w-5 h-5" />
              </div>
              <div className="font-medium text-[#0A2540] dark:text-white text-sm">{q.label}</div>
              <div className="text-[11px] text-[#94A3B8] mt-0.5">{q.sub}</div>
              <ArrowRight className="absolute right-4 bottom-4 w-4 h-4 text-[#94A3B8] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-pb p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="overline">Your work in progress</p>
              <h2 className="font-heading text-xl text-[#0A2540] dark:text-white mt-1">Active project</h2>
            </div>
            <Link to="/dashboard/projects" className="text-sm text-[#C9A356] hover:underline" data-testid="view-all-projects">View all →</Link>
          </div>
          {activeProject ? (
            <div data-testid="active-project">
              <div className="flex flex-wrap gap-3 items-start justify-between mb-4">
                <div>
                  <div className="font-heading text-lg font-medium text-[#0A2540] dark:text-white">{activeProject.title}</div>
                  <div className="text-xs text-[#475569] dark:text-slate-400 mt-1 capitalize">{activeProject.project_type?.replace(/_/g, " ")} · {activeProject.customer_code}</div>
                </div>
                <span className={`text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full ${STATUS_COLOR[activeProject.status] || "bg-slate-200 text-slate-700"}`}>
                  {activeProject.status?.replace(/_/g, " ")}
                </span>
              </div>
              <div className="relative w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2.5 mb-1 overflow-hidden">
                <div className="bg-gradient-to-r from-[#C9A356] to-[#DDBD77] h-2.5 rounded-full transition-all" style={{ width: `${activeProject.progress_pct || 0}%` }}></div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-6 text-right">{activeProject.progress_pct || 0}% complete</div>
              <StatusTimeline timeline={activeProject.timeline || []} status={activeProject.status} />
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#F3E8CE] dark:bg-[#3a2e16] grid place-items-center mb-3">
                <Sun className="w-7 h-7 text-[#C9A356]" />
              </div>
              <p className="text-[#475569] dark:text-slate-400 mb-4 max-w-sm mx-auto">No active projects yet — explore packages to start your solar journey.</p>
              <Link to="/dashboard/packages" className="btn-accent inline-flex" data-testid="explore-packages">
                Explore Packages <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="card-pb p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg text-[#0A2540] dark:text-white inline-flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C9A356]" /> Open invoices
              </h3>
              {openInvoices.length > 0 && <Link to="/dashboard/invoices" className="text-xs text-[#C9A356] hover:underline">All →</Link>}
            </div>
            {openInvoices.length === 0 ? (
              <div className="py-6 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-[#475569] dark:text-slate-400">All caught up</p>
              </div>
            ) : (
              <div className="space-y-2.5" data-testid="invoices-list">
                {openInvoices.slice(0, 3).map((inv) => (
                  <Link key={inv.invoice_id} to="/dashboard/invoices" className="block border border-[#E2E8F0] dark:border-slate-700 rounded-lg p-3 hover:border-[#C9A356] hover:shadow-sm transition group">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0">
                        <div className="text-[10px] font-mono text-[#94A3B8]">#{inv.invoice_id}</div>
                        <div className="font-medium text-sm text-[#0A2540] dark:text-white mt-0.5 truncate">{inv.title}</div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <div className="font-heading font-bold text-[#0A2540] dark:text-white tabular-nums">${inv.amount?.toFixed(2)}</div>
                        <div className="text-[10px] text-[#94A3B8] mt-0.5">Due {inv.due_date || "—"}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="card-pb p-6">
            <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C9A356]" /> Recent activity
            </h3>
            {activity.length === 0 ? (
              <p className="text-sm text-[#475569] dark:text-slate-400">No activity yet</p>
            ) : (
              <ul className="space-y-3" data-testid="activity-feed">
                {activity.map((a) => (
                  <li key={a.activity_id} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#C9A356] mt-2 shrink-0 ring-4 ring-[#C9A356]/15"></div>
                    <div className="min-w-0">
                      <div className="text-[#0A2540] dark:text-white">{a.description}</div>
                      <div className="text-[11px] text-[#94A3B8]">{new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
